import { useQueryClient } from '@tanstack/react-query'
import { Button, Col, Flex, Form, Input, InputNumber, Modal, Row } from 'antd'
import { ModalProps } from 'antd/lib'
import { TFunction } from 'i18next'
import { useEffect } from 'react'

import {
  useClassifierServiceCreateDeviceTypeClassifierElementEndpoint,
  useClassifierServiceGetDeviceTypeClassifierElementEndpoint,
  useClassifierServiceGetDeviceTypeClassifierElementEndpointKey,
  useClassifierServiceGetDeviceTypeClassifierElementListEndpointKey,
  useClassifierServiceUpdateDeviceTypeClassifierElementEndpoint
} from 'src/api/queries'
import { ApiError } from 'src/api/requests'
import { LANGUAGE_KEYS, requiredRules } from 'src/app-constants'
import { EditableFormItem, message, SaveConfirm } from 'src/components'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

type Props = {
  id?: string
  classifierId?: string
  onCancel: () => void
} & ModalProps

const getModalText = (hasId: boolean, t: TFunction) => {
  return {
    title: hasId ? t('equipments.edit_device') : t('equipments.add_device'),
    button: hasId ? t('common.edit') : t('common.save')
  }
}

export const DeviceTypeModal = ({
  id,
  onCancel,
  classifierId,
  ...props
}: Props) => {
  const { t } = useLocales()

  const [form] = Form.useForm()
  const queryClient = useQueryClient()

  const { title, button } = getModalText(!!id, t)

  const { data } = useClassifierServiceGetDeviceTypeClassifierElementEndpoint(
    {
      elementId: String(id)
    },
    [id],
    {
      enabled: !!id
    }
  )

  const { mutate: create, isPending: isCreating } =
    useClassifierServiceCreateDeviceTypeClassifierElementEndpoint({
      onError: (e: ApiError) => formErrorHandler(e.body.errors),
      onSuccess: () => {
        message.success(t('equipments.create_success_device'))
        onMutationSuccess()
      }
    })

  const { mutate: edit, isPending: isEditing } =
    useClassifierServiceUpdateDeviceTypeClassifierElementEndpoint({
      onError: (e: ApiError) => formErrorHandler(e.body.errors),
      onSuccess: () => {
        message.success(t('equipments.edit_success_device'))
        onMutationSuccess()
      }
    })

  const onSave = async () => {
    const values = await form.validateFields()

    return id
      ? edit({ elementId: String(id), requestBody: values })
      : create({ requestBody: values, classifierId: String(classifierId) })
  }

  const onMutationSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: [
        useClassifierServiceGetDeviceTypeClassifierElementListEndpointKey
      ]
    })
    queryClient.invalidateQueries({
      queryKey: [useClassifierServiceGetDeviceTypeClassifierElementEndpointKey]
    })
    onCancel()
  }

  useEffect(() => {
    if (data) {
      form.setFieldsValue(data)
    }
  }, [data])

  return (
    <Modal
      open
      maskClosable={false}
      centered
      title={title}
      footer={false}
      onCancel={onCancel}
      {...props}
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <Row gutter={[24, 24]}>
          {LANGUAGE_KEYS.map((langKey) => {
            return (
              <Col span={12} key={langKey}>
                <EditableFormItem
                  isEditing={true}
                  name={['translation', langKey]}
                  label={`${langKey.toUpperCase()}`}
                  rules={requiredRules}
                  className="full-width"
                >
                  <Input placeholder={t('common.enter_text')} />
                </EditableFormItem>
              </Col>
            )
          })}

          <Col span={12}>
            <Form.Item
              name="inspectionPeriodMonths"
              label={t('equipments.inspection_period')}
              rules={requiredRules}
            >
              <InputNumber className="full-width" min={0} placeholder="0" />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item name="order" label={t('common.order')}>
              <InputNumber className="full-width" min={0} placeholder="0" />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item name="comments" label={t('common.comments')}>
              <Input.TextArea placeholder={t('common.comments')} />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Flex justify="center" gap={8}>
              <Button onClick={onCancel}>{t('common.cancel')}</Button>

              <SaveConfirm
                title={`${button}?`}
                onConfirm={onSave}
                okButtonProps={{ loading: isEditing || isCreating }}
              >
                <Button type="primary">{button}</Button>
              </SaveConfirm>
            </Flex>
          </Col>
        </Row>
      </Form>
    </Modal>
  )
}
